import { Fragment, type ReactElement, type ReactNode } from "react";
import {
  REPORT_KPI_GROUPS,
  REPORT_KPI_UNITS,
  REPORT_SUMMARY_KPIS,
  reportKpiValue,
  reportTargetStatus,
  reportTargetWord,
  type ReportKpiGroupId,
  type ReportKpiId,
  type ReportMetrics,
  type ReportTargets,
} from "@gioia365/domain/report-kpis";
import type { MessageKey } from "@gioia365/i18n";
import { DataTable } from "../design/components/DataTable";
import { Segmented } from "../design/components/Segmented";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { formatKpi, KPI_GROUP_TITLES, KPI_TEXTS, kpiGroupOf, targetOf, targetText, targetWordText } from "./report-format";

// Le tabelle del Report come il foglio (piano M7, Task 3; mock-up `tableAndCards`, `columnsFor`, `TABLE_GROUPS`, `cell`): le colonne a
// gruppi, perché le 23 del foglio non stanno in nessuno schermo («Sintesi», poi Chiamate, Disco, Demo, Chiusure, «Tutte, come nel
// foglio»); sopra 860 px una tabella con i gruppi in testa, sotto le stesse righe come schede, senza scorrimento di lato della pagina.
// Soltanto «Tutte, come nel foglio» è più larga dello schermo e scorre dentro il suo riquadro, con il nome fermo (la prima colonna di
// `DataTable`). Ogni numero è calcolato dai conteggi della riga con le formule del dominio, il totale compreso: le percentuali non sono
// mai la media delle persone. Un obiettivo si legge anche senza colore: ✓ o ↓ e, per lo screen reader, l'obiettivo con la parola.
// Il gruppo di colonne scelto sta nello stato della pagina (D11), non nella tabella: resta quando il periodo o «Di chi» cambiano e i
// numeri si rileggono, anche se intanto la tabella lascia il posto al caricamento.

/** Una riga: una persona, un giorno o il totale, con i suoi conteggi. */
export type KpiRow = { key: string; label: string; sub?: string; metrics: ReportMetrics; total?: boolean };

/** I gruppi di colonne, nell'ordine dei pulsanti. */
export type KpiTableGroup = "summary" | ReportKpiGroupId | "all";
const TABLE_GROUPS: readonly KpiTableGroup[] = ["summary", "calls", "disco", "demo", "closings", "all"];

const GROUP_LABELS: Readonly<Record<KpiTableGroup, MessageKey>> = {
  summary: "reports.table.groups.summary",
  calls: "reports.table.groups.calls",
  disco: "reports.table.groups.disco",
  demo: "reports.table.groups.demo",
  closings: "reports.table.groups.closings",
  all: "reports.table.groups.all",
};

const ALL_KPIS: readonly ReportKpiId[] = REPORT_KPI_GROUPS.flatMap((group) => group.kpis);

/** Le colonne di un gruppo (`columnsFor` del mock-up). */
function columnsOf(group: KpiTableGroup): readonly ReportKpiId[] {
  if (group === "summary") return REPORT_SUMMARY_KPIS;
  if (group === "all") return ALL_KPIS;
  return REPORT_KPI_GROUPS.find((item) => item.id === group)?.kpis ?? [];
}

/** I gruppi del foglio sopra le colonne: uno per ogni serie di colonne vicine dello stesso gruppo. */
function spansOf(columns: readonly ReportKpiId[]): Array<{ group: ReportKpiGroupId; count: number }> {
  const spans: Array<{ group: ReportKpiGroupId; count: number }> = [];
  for (const id of columns) {
    const group = kpiGroupOf(id);
    const last = spans.at(-1);
    if (last && last.group === group) last.count += 1;
    else spans.push({ group, count: 1 });
  }
  return spans;
}

type Props = {
  /** La didascalia della tabella, per gli screen reader. */
  caption: string;
  /** L'intestazione della colonna dei nomi («Commerciale», «Giorno»). */
  firstHead: string;
  /** Il nome del gruppo dei pulsanti delle colonne. */
  groupsLabel: string;
  /** Il titolo e la spiegazione della sezione, a sinistra dei pulsanti delle colonne. */
  heading: ReactNode;
  rows: readonly KpiRow[];
  targets: ReportTargets;
  /** Le schede partono aperte (le persone) o chiuse (i giorni); quella del totale è sempre aperta. */
  openCards: boolean;
  /** Il gruppo di colonne scelto, che la pagina ricorda. */
  group: KpiTableGroup;
  onGroupChange: (group: KpiTableGroup) => void;
  /** Una nota sotto la tabella quando mostra tutte le colonne. */
  wideNote?: string;
};

export function KpiTable({ caption, firstHead, groupsLabel, heading, rows, targets, openCards, group, onGroupChange, wideNote }: Props): ReactElement {
  const t = useT();
  const { locale } = useLocale();
  const columns = columnsOf(group);
  const wide = group === "all";
  // Le schede: con tutte le colonne divise nei quattro gruppi del foglio, altrimenti le colonne scelte.
  const cardParts: Array<{ title: string | null; kpis: readonly ReportKpiId[] }> = wide
    ? REPORT_KPI_GROUPS.map((item) => ({ title: t(KPI_GROUP_TITLES[item.id]), kpis: item.kpis }))
    : [{ title: null, kpis: columns }];

  const choose = (value: string) => {
    const next = TABLE_GROUPS.find((candidate) => candidate === value);
    if (next) onGroupChange(next);
  };

  const headTitle = (id: ReportKpiId) => {
    const texts = KPI_TEXTS[id];
    return texts.how ? t("reports.headTitle", { label: t(texts.label), how: t(texts.how) }) : t(texts.label);
  };

  return (
    <>
      <div className="g-report-head">
        <div>{heading}</div>
        <Segmented label={groupsLabel} options={TABLE_GROUPS.map((value) => ({ value, label: t(GROUP_LABELS[value]) }))} value={group} onChange={choose} />
      </div>
      <div className="g-report-table" data-wide={wide ? "true" : undefined}>
        <DataTable caption={caption} captionHidden>
          <thead>
            <tr>
              <th scope="col" rowSpan={2}>
                {firstHead}
              </th>
              {spansOf(columns).map((span, index) => (
                <th key={`${span.group}-${index}`} scope="colgroup" colSpan={span.count} className="g-report-table__group">
                  {t(KPI_GROUP_TITLES[span.group])}
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((id) => {
                const target = targetOf(targets, id);
                return (
                  <th key={id} scope="col" title={headTitle(id)}>
                    {t(KPI_TEXTS[id].short)}
                    {target ? <span className="g-table__sub">{targetText(t, locale, id, target)}</span> : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className={row.total ? "g-report-total" : undefined}>
                <th scope="row">
                  {row.label}
                  {row.sub ? <span className="g-table__sub">{row.sub}</span> : null}
                </th>
                {columns.map((id) => (
                  <ValueCell key={id} id={id} metrics={row.metrics} targets={targets} />
                ))}
              </tr>
            ))}
          </tbody>
        </DataTable>
      </div>
      {wide && wideNote ? <p className="g-report-footnote g-report-table-note">{wideNote}</p> : null}
      <div className="g-report-cards">
        {rows.map((row) => (
          <details key={row.key} className="g-report-card" data-total={row.total ? "true" : undefined} open={openCards || row.total === true}>
            <summary>
              <span className="g-report-card__name">{row.label}</span>
              <span className="g-report-card__key">
                {t("reports.table.cardKey", {
                  calls: row.metrics.calls === 1 ? t("reports.table.callsOne") : t("reports.table.callsMany", { count: formatCount(locale, row.metrics.calls) }),
                  closings: row.metrics.closings === 1 ? t("reports.table.closingsOne") : t("reports.table.closingsMany", { count: formatCount(locale, row.metrics.closings) }),
                })}
              </span>
            </summary>
            {row.sub ? <p className="g-report-card__sub">{row.sub}</p> : null}
            {cardParts.map((part, index) => (
              <Fragment key={part.title ?? index}>
                {part.title ? <p className="g-report-card__group">{part.title}</p> : null}
                <dl>
                  {part.kpis.map((id) => (
                    <CardValue key={id} id={id} metrics={row.metrics} targets={targets} />
                  ))}
                </dl>
              </Fragment>
            ))}
          </details>
        ))}
      </div>
    </>
  );
}

type ValueProps = { id: ReportKpiId; metrics: ReportMetrics; targets: ReportTargets };

/** Una cella della tabella: il numero e, con un obiettivo, il colore, il segno e la frase nascosta. */
function ValueCell({ id, metrics, targets }: ValueProps) {
  const t = useT();
  const { locale } = useLocale();
  const value = reportKpiValue(id, metrics);
  const text = formatKpi(locale, REPORT_KPI_UNITS[id], value);
  const target = targetOf(targets, id);
  const status = reportTargetStatus(value, target);
  if (value === null || target === undefined || status === null) return <td className="g-report-num">{text}</td>;
  const goal = targetText(t, locale, id, target);
  const word = targetWordText(t, reportTargetWord(value, target));
  return (
    <td className="g-report-num" data-status={status} title={t("reports.target.cell", { target: goal, word })}>
      {text}
      <span className="g-report-mark" aria-hidden="true">
        {status === "ok" ? t("reports.marks.ok") : t("reports.marks.below")}
      </span>
      <span className="g-visually-hidden">{t("reports.target.hidden", { target: goal, word })}</span>
    </td>
  );
}

/** Un numero della scheda di una riga: il nome lungo con l'obiettivo sotto, il numero con il segno. */
function CardValue({ id, metrics, targets }: ValueProps) {
  const t = useT();
  const { locale } = useLocale();
  const value = reportKpiValue(id, metrics);
  const target = targetOf(targets, id);
  const status = reportTargetStatus(value, target);
  return (
    <>
      <dt>
        {t(KPI_TEXTS[id].label)}
        {target ? <span className="g-table__sub">{targetText(t, locale, id, target)}</span> : null}
      </dt>
      <dd data-status={status ?? undefined}>
        {formatKpi(locale, REPORT_KPI_UNITS[id], value)}
        {status !== null && value !== null && target !== undefined ? (
          <>
            <span className="g-report-mark" aria-hidden="true">
              {status === "ok" ? t("reports.marks.ok") : t("reports.marks.below")}
            </span>
            <span className="g-visually-hidden">{t("reports.target.cardHidden", { word: targetWordText(t, reportTargetWord(value, target)) })}</span>
          </>
        ) : null}
      </dd>
    </>
  );
}
